import { useRef, useState } from "react";
import { Alert } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { ConfirmDialog } from "../ConfirmDialog";

interface LogoutModalProps {
  open: boolean;
  onClose: () => void;
  onLogout: () => void | Promise<void>;
}

export default function LogoutModal({ open, onClose, onLogout }: LogoutModalProps) {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutFailed, setLogoutFailed] = useState(false);
  const logoutPending = useRef(false);

  const handleLogout = async () => {
    if (logoutPending.current) return;
    logoutPending.current = true;
    setLogoutFailed(false);
    setIsLoggingOut(true);
    try {
      await onLogout();
      // WorkOS redirects out of the app. Keep the dialog locked until then.
    } catch {
      logoutPending.current = false;
      setIsLoggingOut(false);
      setLogoutFailed(true);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={() => {
        setLogoutFailed(false);
        onClose();
      }}
      onConfirm={handleLogout}
      loading={isLoggingOut}
      tone="error"
      icon={<LogoutIcon />}
      title="Sign Out?"
      description="Are you sure you want to sign out? You'll need to log in again to access your account."
      confirmLabel={isLoggingOut ? "Signing you out..." : "Sign Out"}
    >
      {logoutFailed && (
        <Alert severity="error" sx={{ mt: 2, textAlign: "left" }}>
          Sign out failed. Please try again.
        </Alert>
      )}
    </ConfirmDialog>
  );
}
